import { endpoints, packed, types } from "magnetar-common";
import { Err, Ok, ResultOk } from "@/types/result";
import { resolveHost } from "@/config";
import { broadcastReload } from "@/scripts/broadcast-reload";
import { defineStore } from "pinia";
import { StorageSerializers, useLocalStorage } from "@vueuse/core";
import {
    useEventEmitter,
    useLegacyStreamChannel,
} from "@/composables/use-stream";
import { magApi } from "@/api-client";

export const useAccountStore = defineStore("accounts", {
    state: () => ({
        accounts: useLocalStorage(
            "mag/auth/accounts",
            {} as Record<string, Required<packed.PackUserSelfMaybeAll>>,
            {
                listenToStorageChanges: true,
                deep: true,
            },
        ),
        current: useLocalStorage<string | null>("mag/auth/current", null, {
            listenToStorageChanges: true,
            deep: true,
            serializer: StorageSerializers.string,
        }),
        sessions: useLocalStorage(
            "mag/auth/sessions",
            {} as Record<string, string>,
            {
                listenToStorageChanges: true,
                deep: true,
            },
        ),
    }),
    actions: {
        async addAccount(token: string) {
            const result = await fetchAccount(token);

            if (result.isErr()) {
                return Err(result.unwrapErr());
            }

            const user = result.unwrap();

            this.sessions[token] = user.id;

            if (user.id in this.accounts) {
                this.accounts[user.id] = {
                    ...this.accounts[user.id],
                    ...user,
                };

                return Ok(user);
            }

            this.accounts[user.id] = user;
            return Ok(user);
        },
        async logIn(token: string) {
            const result = await this.addAccount(token);

            if (result.isErr()) {
                return Err(result.unwrapErr());
            }

            this.current = token;

            return Ok(result.unwrap());
        },
        logOut() {
            if (!this.current) {
                return;
            }

            const { [this.current]: id, ...newSessions } = this.sessions;
            this.sessions = newSessions;

            if (
                !Object.values(this.sessions).some(
                    (sessionUserId) => sessionUserId === id,
                )
            ) {
                const { [id]: removedAccount, ...newAccounts } = this.accounts;
                this.accounts = newAccounts;
            }

            this.current = Object.keys(this.sessions)[0] ?? null;

            if (!this.current) {
                broadcastReload("/");
                return;
            }
        },
        removeAccount(id: string) {
            const { [id]: removedAccount, ...newAccounts } = this.accounts;
            this.accounts = newAccounts;

            this.sessions = Object.fromEntries(
                Object.entries(this.sessions).filter(
                    ([, userId]) => userId !== id,
                ),
            );

            this.current = Object.keys(this.sessions)[0] ?? null;
        },
        switchAccount(account: { id: string }) {
            const session = Object.entries(this.sessions).find(
                ([, accountId]) => accountId == account.id,
            );

            if (!session) {
                return;
            }

            this.$patch({
                current: session[0],
            });
        },
        async fetchCurrent() {
            if (!this.current) {
                return Err({
                    cause: "not_logged_in",
                });
            }

            const result = await fetchAccount(this.current);

            result.peek((user) => {
                if (!(user.id in this.accounts)) {
                    return;
                }

                // TODO: If suspended remove it instead

                this.$patch((store) => {
                    store.accounts[user.id] = user;
                });
            });

            return result;
        },
        patchCurrent(u: Partial<packed.PackUserSelfMaybeAll>) {
            this.$patch((state) => {
                const id = this.currentUserId;
                if (!id) {
                    return;
                }

                state.accounts[id] = {
                    ...state.accounts[id],
                    ...u,
                };
            });
        },
    },
    getters: {
        currentUserId: (state): string | null => {
            return state.current
                ? (state.sessions[state.current] ?? null)
                : null;
        },
        currentUser(): Required<packed.PackUserSelfMaybeAll> | null {
            return this.currentUserId
                ? (this.accounts[this.currentUserId] ?? null)
                : null;
        },
        currentUserRequired(): Required<packed.PackUserSelfMaybeAll> {
            if (!this.currentUserId) {
                throw new Error("Assertion failed: User is required here");
            }

            const user = this.accounts[this.currentUserId];

            if (!user) {
                throw new Error("Assertion failed: User is required here");
            }

            return user;
        },
        isLoggedIn: (state) => {
            return state.current != null;
        },
        isCurrentUser() {
            return (user?: Pick<types.UserBase, "username" | "host"> | null) =>
                this.currentUser != null &&
                user != null &&
                this.currentUser.username === user.username &&
                !resolveHost(user.host);
        },
        currentUserRole(): "administrator" | "moderator" | "normal" | null {
            return this.currentUser == null
                ? null
                : this.currentUser.is_admin
                  ? "administrator"
                  : this.currentUser.is_moderator
                    ? "moderator"
                    : "normal";
        },
    },
});

export type AccountFetchResult = ResultOk<
    Required<packed.PackUserSelfMaybeAll>,
    | {
          cause: "suspended";
      }
    | {
          cause: "error";
          error: string;
      }
>;

export const fetchAccount = async (
    token: string,
): Promise<AccountFetchResult> => {
    return (await magApi(
        endpoints.GetUserSelf,
        {
            detail: true,
            pins: true,
            profile: true,
            secrets: true,
            self_detail: true,
        },
        {},
        token,
    )
        .then((u) => {
            if (u.is_suspended) {
                return Err({
                    cause: "suspended",
                });
            }

            return Ok(u as Required<packed.PackUserSelfMaybeAll>);
        })
        .catch((e) =>
            Err({
                cause: "error",
                error: `[${e?.status}] ${e?.code}`,
            }),
        )) as AccountFetchResult;
};

export const useAccountEventBus = (
    accountStore: ReturnType<typeof useAccountStore>,
) => {
    const main = useLegacyStreamChannel("main", null, "System");

    useEventEmitter(main, "meUpdated", async (a) => {
        await accountStore.fetchCurrent();
    });

    useEventEmitter(main, "myTokenRegenerated", () => {
        // accountStore.logOut();
    });
};
